import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityTemplateService } from '../activity-template/activity-template.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { SourceTypesLookup } from '@go-modules/interfaces/source-type-lookup';
import { TitleDescription } from '@go-modules/interfaces/title-description';
import { NgxSourceMediaPanelComponent } from '@go-modules/components/sidepanels/activity-editor/source-media-panel/source-media-panel.component';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { ActivitySourceMediaModel } from 'go-modules/models/activity/activity-source-media';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryDialogComponent } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';

@Component({
	selector: 'source-media-setting',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		MatSelectModule,
		NgxSourceMediaPanelComponent
	],
	templateUrl: './source-media-setting.component.html',
	styleUrls: ['./source-media-setting.component.scss'],
	providers: [ActivityTemplateService],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceMediaSettingComponent implements OnInit {
	@Input() public show = false;
	@Input() public showSourceMediaButton = false;
	@Input() public showSourceTypeDropdown = false;
	@Input() public activity: ActivityModel;

	public sourceTypesLookup: SourceTypesLookup;
	public sourceTypes: TitleDescription[];
	public sourceTypeControl = new FormControl<string | null>(null);

	constructor (
		public activityFormService: ActivityFormService,
		@Inject(mediaToken) private MediaModel,
		private cdr: ChangeDetectorRef,
		private ngxGoModalService: GoModalService
	) {
		this.sourceTypesLookup = {
			INSTRUCTOR_SELECTED: {
				title: 'activity-editor-new_source-media-instructor-selected-title',
				description: 'activity-editor-new_source-media-instructor-selected-description'
			},
			PRESENTER_SELECTED: {
				title: 'activity-editor-new_source-media-presenter-selected-title',
				description: 'activity-editor-new_source-media-presenter-selected-description'
			}
		};

		this.sourceTypes = [
			this.sourceTypesLookup.INSTRUCTOR_SELECTED,
			this.sourceTypesLookup.PRESENTER_SELECTED
		];
	}

	public ngOnInit () {
		const sourceType = this.activityFormService.getControl('has_self_select_source_media').value
			? this.sourceTypesLookup.PRESENTER_SELECTED.title
			: (
				this.activityFormService.getControl('source_media').value
					? this.sourceTypesLookup.INSTRUCTOR_SELECTED.title
					: null
			);

		this.sourceTypeControl.setValue(sourceType);
	}

	public onSourceTypeChange (change: MatSelectChange): void {
		this.sourceTypeControl.setValue(change.value);

		const hasSelfSelectSourceMediaControl = this.activityFormService.getControl('has_self_select_source_media');

		if (this.sourceTypeControl.value === this.sourceTypesLookup.INSTRUCTOR_SELECTED.title) {
			hasSelfSelectSourceMediaControl.setValue(false);

			this.openSourceMediaModal();
		}

		if (this.sourceTypeControl.value === this.sourceTypesLookup.PRESENTER_SELECTED.title) {
			this.activityFormService.getControl('source_media').setValue(null);
			hasSelfSelectSourceMediaControl.setValue(true);
		}
	}

	public handleOnRemoveSourceMedia () {
		this.activityFormService.getControl('source_media').setValue(null);
		this.openSourceMediaModal();
	}

	public openSourceMediaModal () {
		const form = this.activityFormService.getForm();

		this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
				mode: MODES.SELECT,
				previewable: true,
				collectionManagement: true
			}
		}).afterClosed().subscribe({
			next: (res: any) => {
				if (res) {
					const media = this.MediaModel.model(res);
					const sourceMedia = ActivitySourceMediaModel.create({ media, media_id: media.media_id });

					this.updateSourceMedia(sourceMedia);

					if (this.activityFormService.getControl('source_media').value) {
						this.sourceTypeControl.markAsDirty();
					} else {
						form.markAsDirty();
					}
				}
			},
			error: () => {
				this.removeSourceMedia();
			}
		});
	}

	public handleMediaSync (event) {
		this.updateSourceMedia(event);
	}

	public getSelectedSourceType (): TitleDescription | undefined {
		const selectedKey = this.sourceTypeControl.value;
		if (!selectedKey) return undefined;
		return this.sourceTypes.find((s) => s.title === selectedKey);
	}

	private removeSourceMedia (): void {
		this.activityFormService.getControl('source_media').setValue(null);
		this.sourceTypeControl.setValue(null);
	}

	private updateSourceMedia (sourceMedia): void {
		const sourceMediaControl = this.activityFormService.getControl('source_media');
		sourceMediaControl.setValue(sourceMedia);
		this.cdr.markForCheck();
	}
}
